/**
 * 用户状态管理 Store
 * 管理用户信息、账号列表、登录状态、权限判断等
 */
import { useConfig } from '@rendora/common/composables/useConfig';
import { defineStore } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { message } from '../composables/message';
import {
  deleteUserSession,
  getAccountInfo,
  LoginInfo,
  postAccountList,
  postAccountUpdateInfo,
  postSwitchLogin,
  USER_VERIFY_STATUS,
  type Account,
  type Session,
  type UserInfoVO,
} from '../entities/user';
import { getI18nService } from '../services/i18n';
import { IRightsType, USER_ADMIN } from '../types/enums';
import { isPureHost } from '../utils/config';
import { getCookie, removeCookie, setCookie } from '../utils/cookie';
import { commonEmitter, COMMON_EVENTS } from '../utils/emitter';
import { getPlatformJumpLink } from '../utils/url';
import { localDeleteItem, localSetItem } from '../utils/webStorage';

// 在 Web 环境创建会话
const loginForWeb = (session: Session) => {
  if (import.meta.env.DEV) return;
  return fetch(`/api/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(session),
    credentials: 'include',
  });
};

// 在 Web 环境删除会话
const logoutForWeb = () => {
  if (import.meta.env.DEV) return;
  return fetch(`/api/session`, {
    credentials: 'include',
    method: 'DELETE',
  });
};

// 在后端删除用户会话
const logoutForBe = () => {
  if (import.meta.env.DEV) return;
  return deleteUserSession();
};

export const useUserStore = defineStore('user', () => {
  const user = ref<UserInfoVO>();
  const accountList = ref<Account[]>([]);
  const isSwitching = ref(false);
  const { isUS } = useConfig();

  // token 同时存储在 cookie 和 localStorage
  const token = computed<Session>({
    get() {
      return getCookie('youyan-user-token') as Session;
    },
    set(value: Session) {
      setCookie('youyan-user-token', value);
      localSetItem('youyan-user-token', value);
    },
  });

  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const res = await getAccountInfo();
      setUserInfo(res);
      return res;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch user info:', error);
      throw error;
    }
  };

  // 设置用户信息
  const setUserInfo = (res: UserInfoVO) => {
    user.value = res;
  };

  // 更新用户信息
  const updateUserInfo = async (data: { account_name?: string; logo_path?: string }) => {
    await postAccountUpdateInfo(data);
  };

  // 获取账号列表
  const fetchAccountList = async () => {
    const res = await postAccountList();
    accountList.value = res;
  };

  // 设置账号列表
  const setAccountList = (res: Account[]) => {
    accountList.value = res;
  };

  // 设置会话信息
  const setSession = async (res: Session) => {
    await loginForWeb(res);
    token.value = res;
  };

  // 清除用户数据和会话
  const clearUser = () => {
    removeCookie('youyan-user-token');
    localDeleteItem('youyan-user-token');
    logoutForBe();
    logoutForWeb();
  };

  // 退出登录
  const logout = (logoutUrl?: string) => {
    commonEmitter.emit(COMMON_EVENTS.BEFORE_ACCOUNT_CHANGE);
    clearUser();
    setTimeout(() => {
      if (import.meta.env.DEV || (isPureHost() && !import.meta.client)) {
        if (location.pathname !== '/workspace/login') {
          location.replace('/workspace/login');
        }
      } else {
        // 双重编码避免跳转参数丢失
        const redirectLink = getPlatformJumpLink(
          `/login?redirect=${encodeURIComponent(encodeURIComponent(`${window.location.origin}/workspace`))}`,
        );
        location.replace(logoutUrl || redirectLink);
      }
    }, 200);
  };

  // 切换账号
  const switchAccount = async (account: Account | LoginInfo, noReload?: boolean) => {
    const { t: $t } = getI18nService();
    message.warning($t('zheng-zai-qie-huan-dao-zhang-hao-accountname', [account.name]));

    if (isSwitching.value) {
      return message.error($t('zheng-zai-qie-huan-zhang-hao-qing-shao-hou-zai-shi'));
    }

    isSwitching.value = true;

    if (!account.is_active_user) {
      isSwitching.value = false;
      return;
    }

    if (account.is_expired && account.permission === USER_ADMIN.NORMAL) {
      isSwitching.value = false;
      return message.error($t('tao-can-yi-guo-qi'));
    }

    try {
      // LoginInfo 已有 session，Account 需要获取
      if ('key' in account) {
        await setSession({
          key: account.key,
          expire_time: account.expire_time,
          expire_in: account.expire_in,
          session_id: account.session_id,
          user_id: account.user_id,
        });
      } else {
        const accountWithSession = (await postSwitchLogin(account.user_id))[0];

        if (!accountWithSession) {
          isSwitching.value = false;
          return message.error($t('qie-huan-zhang-hao-shi-bai'));
        }

        await setSession({
          key: accountWithSession.key,
          expire_time: accountWithSession.expire_time,
          expire_in: accountWithSession.expire_in,
          session_id: accountWithSession.session_id,
          user_id: accountWithSession.user_id,
        });
      }

      isSwitching.value = false;
      message.success($t('qie-huan-zhang-hao-cheng-gong'));

      commonEmitter.emit(COMMON_EVENTS.BEFORE_ACCOUNT_CHANGE);
      if (!noReload) {
        window.location.reload();
      }
    } catch (error) {
      isSwitching.value = false;
      throw error;
    }
  };

  // 是否为团队权限
  const isTeamRight = computed(() => {
    if (!user.value?.rights_type) return false;
    const rightsType = Number(user.value.rights_type);
    return [
      IRightsType.ORG_STANDARD,
      IRightsType.ORG_FLAGSHIP,
      IRightsType.TEAM_LIGHT,
      IRightsType.TEAM_TASTE,
    ].includes(rightsType);
  });

  // 是否为个人权限
  const isPersonRight = computed(() => {
    return [IRightsType.PERSONAL_LITE, IRightsType.TASTE, IRightsType.PERSONAL].includes(
      user.value?.rights_type || IRightsType.PERSONAL_LITE,
    );
  });

  // 是否为个人体验版
  const isPersonalFree = computed(() => {
    return user.value?.rights_type === IRightsType.TASTE;
  });

  // 是否为个人轻量版
  const isPersonalLite = computed(() => {
    return user.value?.rights_type === IRightsType.PERSONAL_LITE;
  });

  // 是否为个人入门和标准
  const isPersonLiteOrStandard = computed(() => {
    return [IRightsType.PERSONAL_LITE, IRightsType.PERSONAL].includes(
      user.value?.rights_type || IRightsType.PERSONAL_LITE,
    );
  });

  // 是否为企业旗舰版
  const isEnterpriseFlagship = computed(() => {
    return user.value?.rights_type === IRightsType.ORG_FLAGSHIP;
  });

  // 是否为管理员（permission !== 1 表示非普通成员）
  const isAdmin = computed(() => {
    if (!user.value?.permission) return false;
    return user.value.permission !== 1; // 1 表示普通成员
  });

  // 是否已实名认证
  const isVerified = computed(() => {
    if (!user.value) {
      return false;
    }
    return user.value.identify_info?.status === USER_VERIFY_STATUS.SUCCESS;
  });

  // 是否可对公转账：团队账号需 admin，个人账号需购买团队套餐
  const canCorporateTransfer = (rightType?: IRightsType) => {
    if (isUS) return false;
    if (isTeamRight.value && isAdmin.value) return true;
    if (!isTeamRight.value && rightType) {
      return [IRightsType.TEAM_LIGHT, IRightsType.ORG_STANDARD, IRightsType.ORG_FLAGSHIP].includes(
        rightType,
      );
    }
    return false;
  };

  // 是否可升级套餐
  const canUpgrade = computed(() => {
    if (!user.value) {
      return false;
    }
    return [IRightsType.PERSONAL_LITE].includes(user.value.rights_type);
  });

  // 是否可创建团队（无团队体验版账号时可创建）
  const canCreateTeam = computed(() => {
    const tasteAccounts = accountList.value.filter(
      item =>
        Number(item.rights_type) === IRightsType.TEAM_TASTE &&
        Number(item.permission) !== USER_ADMIN.NORMAL,
    );
    return tasteAccounts.length === 0;
  });

  // 角色免费时长是否已过期（-1 表示无限制，个人体验版不受限制）
  const isCharacterFreeTimeExpired = computed(() => {
    const onlineRoomUseTimeLimit = user.value?.rights_record.online_room_use_time_limit;
    const alreadyUsedOnlineTime = user.value?.already_used_rights.already_used_online_time;
    if (onlineRoomUseTimeLimit === -1) return false;
    if (isPersonalFree.value) return false;
    return (alreadyUsedOnlineTime || 0) >= (onlineRoomUseTimeLimit || 0);
  });

  // 监听跨标签页用户信息更新（仅客户端环境）
  if (import.meta.client) {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'update_userinfo') {
        fetchUserInfo();
      }
    };

    onMounted(() => {
      window.addEventListener('storage', handleStorage);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('storage', handleStorage);
    });
  }

  return {
    user,
    token,
    accountList,
    setUserInfo,
    fetchAccountList,
    fetchUserInfo,
    clearUser,
    logout,
    updateUserInfo,
    switchAccount,
    setAccountList,
    setSession,
    isTeamRight,
    isAdmin,
    isVerified,
    isCharacterFreeTimeExpired,
    canUpgrade,
    canCorporateTransfer,
    canCreateTeam,
    isEnterpriseFlagship,
    isPersonRight,
    isPersonalFree,
    isPersonalLite,
    isPersonLiteOrStandard,
  };
});
